import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { User, Menu, Sun, Moon, HelpCircle, SlidersHorizontal } from 'lucide-react';
import { HelpModal } from '@/components/help/HelpModal';
import { usePreferencesStore } from '@/store/preferencesStore';
import { preferencesApi } from '@/api/preferencesApi';

interface TopBarProps {
  onMenuClick?: () => void;
}

export function TopBar({ onMenuClick }: TopBarProps) {
  const user = useAuthStore((state) => state.user);
  const [helpOpen, setHelpOpen] = useState(false);
  const theme = usePreferencesStore((s) => s.theme);
  const setTheme = usePreferencesStore((s) => s.setTheme);
  const isDark = theme === 'dark';

  const toggleTheme = () => {
    const next = isDark ? 'light' : 'dark';
    setTheme(next);
    preferencesApi.updateMine({ theme: next }).catch(() => {});
  };

  return (
    <header className="h-16 bg-white dark:bg-charcoal-800 border-b border-gray-200 dark:border-charcoal-700 flex items-center justify-between px-4 lg:px-6">
      {/* Left side */}
      <div className="flex items-center gap-3">
        {/* Hamburger — mobile only */}
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 rounded-lg text-gray-600 dark:text-warm-sand hover:text-gray-900 dark:hover:text-gray-900 dark:text-warm-cream hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 transition-colors"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-sans font-semibold text-gray-900 dark:text-warm-cream">
          Welcome back, {user?.firstName || 'User'}
        </h2>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-3">
        {/* Help button */}
        <button
          onClick={() => setHelpOpen(true)}
          className="p-2 rounded-lg text-gray-600 dark:text-warm-sand hover:text-gray-900 dark:hover:text-gray-900 dark:text-warm-cream hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 transition-colors"
          aria-label="Open help"
          title="Help Center"
        >
          <HelpCircle className="w-5 h-5" />
        </button>

        {/* Theme toggle */}
        <Link
          to="/settings?tab=preferences"
          className="p-2 rounded-lg text-gray-600 dark:text-warm-sand hover:text-gray-900 dark:hover:text-warm-cream hover:bg-gray-100 dark:hover:bg-charcoal-700 transition-colors"
          aria-label="My preferences"
          title="My Preferences"
        >
          <SlidersHorizontal className="w-5 h-5" />
        </Link>
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg text-gray-600 dark:text-warm-sand hover:text-gray-900 dark:hover:text-gray-900 dark:text-warm-cream hover:bg-gray-100 dark:hover:bg-gray-100 dark:bg-charcoal-700 transition-colors"
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>

        {/* User badge */}
        <div className="flex items-center gap-3 bg-gray-100 dark:bg-charcoal-700 rounded-lg px-4 py-2">
          <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-450/20 flex items-center justify-center">
            <User className="w-4 h-4 text-amber-600 dark:text-amber-450" />
          </div>
          <div className="text-sm hidden sm:block">
            <p className="font-medium text-gray-900 dark:text-warm-cream">{user?.fullName}</p>
            <p className="text-xs text-gray-500 dark:text-warm-sand capitalize">{user?.role?.toLowerCase()}</p>
          </div>
        </div>
      </div>
      
      {/* Help Modal */}
      <HelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
    </header>
  );
}
